import { useState, useEffect, useMemo, type CSSProperties } from 'react'
import { NavLink } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useOperatorStore } from '@/shared/stores'
import { IS_TAURI } from '@/lib/platform'
import { useGoogleAuthStore } from '@/features/auth/stores/google-auth-store'
import { useQueueStore } from '@/features/queue/store'
import { isToolApproval, isPRReview } from '@/features/queue/types'
import streamIcon from '@/assets/stream-icon.jpg'

interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
  /**
   * The gateway scope(s) required to access this page. When set, the sidebar
   * renders a lock icon and reduced opacity for operators that lack the scope.
   * The item remains clickable — ScopeGate in routes.tsx shows the denial panel.
   * Omit for pages that are accessible without a specific scope gate.
   * (Task 3552c)
   */
  requiredScope?: string | string[]
}

interface NavSection {
  title: string
  items: NavItem[]
}

const STORAGE_KEY = 'stream:sidebar-collapsed'

/**
 * Convert a route path into the slug used in `app-shell-nav-link-<slug>` test-ids.
 * Strips the leading `/` and replaces inner `/` separators with `-`.
 * See `apps/stream/TESTING.md` for the convention.
 *
 * Examples:
 *   '/agents'      -> 'agents'
 *   '/agents/live' -> 'agents-live'
 *   '/email-health' -> 'email-health'
 */
function navLinkTestId(path: string): string {
  const trimmed = path.replace(/^\//, '')
  const slug = trimmed.replace(/\//g, '-')
  return `app-shell-nav-link-${slug}`
}

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    // Guard against corrupted persistence: must be a plain object of boolean flags.
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const result: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean') result[key] = value
    }
    return result
  } catch { return {} }
}

function saveCollapsed(state: Record<string, boolean>) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch {}
}

const sections: NavSection[] = [
  {
    title: 'Health',
    items: [
      { label: 'System Health', path: '/health', icon: <HealthIcon />, requiredScope: 'infra:read' },
      { label: 'Events', path: '/events', icon: <EventsIcon />, requiredScope: 'events:read' },
    ],
  },
  {
    title: 'Customers',
    items: [
      { label: 'Customer', path: '/customer', icon: <CustomerIcon />, requiredScope: 'user:read' },
      { label: 'Directory', path: '/customers', icon: <CustomersIcon />, requiredScope: 'user:read' },
      { label: 'Deletions', path: '/customers/deletions', icon: <DeletionsIcon />, requiredScope: 'user:read' },
    ],
  },
  {
    title: 'Operator',
    items: [
      // Queue link uses the bare /queue prefix so the sidebar entry stays
      // highlighted across both top-level views (Attention and Responses).
      // Clicking it lands the user on /queue, which routes.tsx redirects
      // to /queue/attention/all (the default landing view).
      { label: 'Attention Queue', path: '/queue', icon: <QueueIcon />, requiredScope: 'operations:read' },
      // Runs sits directly above Automations.  The page has Live (default)
      // and Previous tabs (``?tab=live`` / ``?tab=previous``).  See routes.tsx
      // for the ordering note (the literal path must precede the
      // parameterised ``automations/:id/...`` routes in the router).
      { label: 'Runs', path: '/automations/runs', icon: <RunsIcon />, requiredScope: 'operations:admin' },
      { label: 'Automations', path: '/automations', icon: <AutomationsIcon />, requiredScope: 'operations:admin' },
    ],
  },
  {
    title: 'Communications',
    items: [
      { label: 'E-Mail Inbox', path: '/inbox', icon: <InboxIcon />, requiredScope: 'comms:read' },
      // Review Inbox — App Store review triage surface. Gated by the page-lock
      // manifest until coverage is verified.
      { label: 'Review Inbox', path: '/reviews', icon: <FeedbackIcon />, requiredScope: 'catalog:read' },
      { label: 'E-Mail Templates', path: '/email-templates', icon: <EmailIcon />, requiredScope: 'comms:all' },
      { label: 'Mailgun Delivery', path: '/email-health', icon: <EmailHealthIcon />, requiredScope: 'infra:read' },
    ],
  },
  {
    title: 'Engineering',
    items: [
      { label: 'Tickets', path: '/engineering', icon: <EngineeringIcon />, requiredScope: 'engineering:read' },
      { label: 'Log Feature', path: '/log-feature', icon: <LogFeatureIcon />, requiredScope: 'engineering:write' },
    ],
  },
  {
    title: 'Commerce',
    items: [
      { label: 'Purchases', path: '/purchases', icon: <PurchasesIcon />, requiredScope: 'payments:read' },
      { label: 'Sign Up', path: '/signups', icon: <SignUpIcon />, requiredScope: 'payments:read' },
      { label: 'Catalog', path: '/catalog', icon: <CatalogIcon />, requiredScope: 'catalog:read' },
      { label: 'Offering Templates', path: '/offering-templates', icon: <OfferingTemplatesIcon />, requiredScope: 'catalog:read' },
    ],
  },
  {
    title: 'Agents',
    items: [
      { label: 'Overview', path: '/agents', icon: <AgentsIcon />, requiredScope: 'agents:admin' },
      { label: 'Playground', path: '/playground', icon: <PlaygroundIcon />, requiredScope: 'agents:admin' },
      { label: 'Sessions', path: '/sessions', icon: <SessionsIcon />, requiredScope: 'agents:admin' },
      { label: 'Artifacts', path: '/artifacts', icon: <ArtifactsIcon />, requiredScope: 'agents:admin' },
      { label: 'Memory', path: '/memory', icon: <MemoryIcon />, requiredScope: 'agents:admin' },
      // Skill Library — browse Stream-bundled, global, and project skills.
      // Lives here (Agents section) because skills extend what agents can
      // do; see src/features/skills/. Skills browsing is a local file operation;
      // no gateway scope required.
      { label: 'Skills', path: '/skills', icon: <SkillsIcon /> },
      // Audit Trails is per-agent (route /agents/:id/audit) — reached from the
      // agent detail page, not a top-level entry.
    ],
  },
  {
    title: 'Analytics',
    items: [
      // Insights — cross-portfolio analytics. Ships in BOTH the desktop and
      // the browser-portal builds (unlike /reports, which is desktop-only),
      // because the people who read these numbers work in a browser.
      //
      // The scope is `insights:dashboard:read` verbatim: that is exactly what
      // the gateway requires on all 30 /v1/admin/insights/* routes, and
      // Stream's client-side scope mirror does not implement the gateway's
      // ScopeImplications, so a broader admin scope would not satisfy it here.
      //
      // The link points at /insights, which redirects to the Overview page —
      // one of the two that read live data.
      { label: 'Insights', path: '/insights', icon: <InsightsIcon />, requiredScope: 'insights:dashboard:read' },
      // Reports Hub — browse and open analytics reports published to the
      // shared /Kodeon Analytics/ Google Drive folder. No gateway scope
      // required — Drive access is the permission gate.
      { label: 'Reports', path: '/reports', icon: <ReportsIcon /> },
    ],
  },
  {
    title: 'Platform',
    items: [
      // Staff Management — invite, edit, revoke staff access.
      // Requires identity:admin scope; operators without it will see the
      // ScopeGate denial panel (task 3552a + 3552c).
      { label: 'Staff', path: '/staff', icon: <StaffIcon />, requiredScope: 'identity:admin' },
    ],
  },
]

export function Sidebar() {
  const selectedOperator = useOperatorStore((s) => s.selected)
  const clearOperator = useOperatorStore((s) => s.clearOperator)
  const signOut = useGoogleAuthStore((s) => s.signOut)
  // Desktop: return to the OperatorSelector, which carries the Google sign-out.
  // Browser: the OperatorSelector is never shown (the signed-in identity IS the
  // operator), so this badge is the only way out and must end the Google
  // session itself — otherwise the stored token signs the operator straight
  // back in on the next load.
  //
  // Browser order matters: sign out BEFORE clearing. Clearing first, while
  // still authenticated, makes App re-resolve the profile of the account that
  // is signing out, and the next account to sign in would inherit it.
  const handleOperatorBadge = () => {
    if (IS_TAURI) {
      clearOperator()
      return
    }
    void signOut().finally(clearOperator)
  }
  const hasScope = useOperatorStore((s) => s.hasScope)
  // The sidebar badge counts only "attention" items (tool approvals + PR
  // reviews) — i.e. items that show up under /queue/attention/*. Text
  // reviews (which live under /queue/responses/*) are deliberately excluded:
  // they are conversational follow-ups rather than blocking decisions and
  // do not warrant their own sidebar badge.
  const attentionCount = useQueueStore((state) =>
    state.attentionItems.reduce(
      (n, item) =>
        isToolApproval(item.attention_type) || isPRReview(item.attention_type) ? n + 1 : n,
      0,
    ),
  )
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed)
  const [iconError, setIconError] = useState(false)
  const [operatorHover, setOperatorHover] = useState(false)
  const [operatorFocus, setOperatorFocus] = useState(false)

  useEffect(() => { saveCollapsed(collapsed) }, [collapsed])

  // Deterministic gradient per consumer_id, mirroring the splash so the same
  // operator reads as the same person across surfaces.
  const operatorGradient = useMemo(() => {
    if (!selectedOperator?.consumer_id) return null
    let hash = 0
    for (let i = 0; i < selectedOperator.consumer_id.length; i++) {
      hash = (hash << 5) - hash + selectedOperator.consumer_id.charCodeAt(i)
      hash |= 0
    }
    const hue = Math.abs(hash) % 360
    // Single-hue gradient: deeper shade → lighter shade of the same color.
    return `linear-gradient(135deg, hsl(${hue}, 72%, 42%) 0%, hsl(${hue}, 65%, 62%) 100%)`
  }, [selectedOperator?.consumer_id])

  const toggleSection = (title: string) => {
    setCollapsed(prev => ({ ...prev, [title]: !prev[title] }))
  }

  return (
    <aside style={styles.sidebar} data-trace-shell="sidebar">
      {/* Logo */}
      <div style={styles.logoArea}>
        {iconError ? (
          <div style={styles.logoIconFallback}>
            <StreamIcon />
          </div>
        ) : (
          <img
            src={streamIcon}
            alt="Stream"
            style={styles.logoIcon}
            onError={() => setIconError(true)}
          />
        )}
        <span style={styles.logoText}>Stream</span>
      </div>

      {/* Navigation */}
      <nav style={styles.nav}>
        {sections.map((section, idx) => {
          const isCollapsed = !!collapsed[section.title]
          return (
            <div key={section.title} style={idx > 0 ? { marginTop: '16px' } : undefined}>
              <button
                style={styles.sectionButton}
                onClick={() => toggleSection(section.title)}
                aria-expanded={!isCollapsed}
              >
                <div style={styles.sectionLine} />
                <span style={styles.sectionTitle}>{section.title}</span>
                <div style={styles.sectionLineFlex} />
                <span style={{
                  ...styles.chevron,
                  transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                }}>
                  <ChevronIcon />
                </span>
              </button>
              {!isCollapsed && (
                <ul style={styles.navList}>
                  {section.items.map((item) => {
                    const showBadge = item.path === '/queue' && attentionCount > 0
                    // An item is locked when it declares a required scope and the
                    // current operator does not have it. Locked items remain
                    // clickable — ScopeGate in routes.tsx shows the denial panel.
                    const locked = item.requiredScope ? !hasScope(item.requiredScope) : false
                    return (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          // ``end`` forces exact-path matching so a parent
                          // route doesn't stay highlighted when a child path
                          // is active.  /automations is in this set because
                          // /automations/runs is a sibling sidebar entry —
                          // without ``end``, both rows light up at once.
                          end={item.path === '/agents' || item.path === '/customers' || item.path === '/automations'}
                          className={({ isActive }) => cn('nav-link', isActive && 'nav-link-active')}
                          style={({ isActive }) => ({
                            ...styles.navLink,
                            ...(isActive ? styles.navLinkActive : {}),
                            ...(locked ? { opacity: 0.5 } : {}),
                          })}
                          title={locked && item.requiredScope
                            ? `Requires scope: ${Array.isArray(item.requiredScope) ? item.requiredScope.join(', ') : item.requiredScope}`
                            : undefined
                          }
                          data-testid={navLinkTestId(item.path)}
                        >
                          <span style={styles.navIcon}>{item.icon}</span>
                          {item.label}
                          {showBadge && (
                            <span style={styles.navBadge}>{attentionCount}</span>
                          )}
                          {locked && (
                            <Lock style={{ marginLeft: 'auto', width: 12, height: 12, flexShrink: 0 }} />
                          )}
                        </NavLink>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )
        })}
      </nav>

      {/* Settings and Operator */}
      <div style={styles.footer}>
        <NavLink
          to="/settings"
          className={({ isActive }) => cn('nav-link', isActive && 'nav-link-active')}
          style={({ isActive }) => ({
            ...styles.navLink,
            ...(isActive ? styles.navLinkActive : {}),
          })}
          data-testid="app-shell-nav-link-settings"
        >
          <span style={styles.navIcon}>
            <SettingsIcon />
          </span>
          Settings
        </NavLink>

        {/* Operator identity — click to return to the sign-in screen. This is
            the single canonical "back to sign-in" affordance in the app.
            Hover reveals a hint in place of the consumer_id. With 1-to-1
            Google→staff binding, re-signing in lands on the same profile.
            To switch accounts, sign out of Google first via the badge on the
            sign-in screen. */}
        {selectedOperator && (
          <button
            type="button"
            className="operator-switch-button"
            onClick={handleOperatorBadge}
            onMouseEnter={() => setOperatorHover(true)}
            onMouseLeave={() => setOperatorHover(false)}
            onFocus={() => setOperatorFocus(true)}
            onBlur={() => setOperatorFocus(false)}
            aria-label={`Signed in as ${selectedOperator.display_name}. Click to ${IS_TAURI ? 'return to sign-in' : 'sign out'}.`}
            data-testid="sidebar-operator-switch-button"
            style={{
              ...styles.operatorCard,
              ...(operatorHover || operatorFocus ? styles.operatorCardHover : {}),
            }}
          >
            <div style={styles.operatorContent}>
              <div
                aria-hidden
                style={{
                  ...styles.avatarFallback,
                  background: operatorGradient ?? styles.avatarFallback.background,
                }}
              >
                {selectedOperator.initials || selectedOperator.display_name.charAt(0).toUpperCase()}
              </div>
              <div style={styles.operatorInfo}>
                <span style={styles.operatorName}>
                  {selectedOperator.display_name}
                </span>
                <span style={styles.operatorEmail}>
                  {operatorHover || operatorFocus
                    ? IS_TAURI ? 'Back to sign-in' : 'Sign out'
                    : selectedOperator.consumer_id}
                </span>
              </div>
              <span
                aria-hidden
                style={{
                  ...styles.operatorSwitchIcon,
                  opacity: operatorHover || operatorFocus ? 1 : 0,
                }}
              >
                <SwitchUserIcon />
              </span>
            </div>
          </button>
        )}
      </div>
    </aside>
  )
}

const styles: Record<string, CSSProperties> = {
  sidebar: {
    display: 'flex',
    width: '208px',
    flexDirection: 'column',
    borderRight: '1px solid var(--border-subtle)',
    background: 'var(--surface-chrome)',
    backdropFilter: 'blur(24px)',
    position: 'relative',
    zIndex: 10,
  },
  logoArea: {
    display: 'flex',
    height: '56px',
    alignItems: 'center',
    gap: '10px',
    borderBottom: '1px solid var(--border-subtle)',
    padding: '0 20px',
  },
  logoIcon: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    objectFit: 'cover' as const,
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
  },
  logoIconFallback: {
    display: 'flex',
    width: '28px',
    height: '28px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    background: 'var(--accent-gradient)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
  },
  logoText: {
    fontSize: '15px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
  },
  nav: {
    flex: 1,
    overflow: 'auto',
    padding: '12px 12px',
  },
  sectionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
    padding: '2px 8px',
    width: '100%',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'inherit',
  },
  sectionLine: {
    width: '12px',
    height: '1px',
    background: 'var(--sidebar-divider)',
  },
  sectionTitle: {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: 'var(--text-muted)',
  },
  sectionLineFlex: {
    flex: 1,
    height: '1px',
    background: 'var(--sidebar-divider-soft)',
  },
  chevron: {
    display: 'flex',
    width: '12px',
    height: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 200ms',
    color: 'var(--text-muted)',
  },
  navList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    transition: 'all 200ms',
  },
  navLinkActive: {
    background: 'rgba(59, 130, 246, 0.1)',
    color: 'var(--accent-primary)',
  },
  navIcon: {
    display: 'flex',
    width: '16px',
    height: '16px',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
  },
  navBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '18px',
    height: '18px',
    padding: '0 5px',
    marginLeft: 'auto',
    fontSize: '10px',
    fontWeight: 600,
    color: 'white',
    background: 'var(--accent-primary)',
    borderRadius: '9px',
  },
  footer: {
    borderTop: '1px solid var(--border-subtle)',
    padding: '12px',
  },
  operatorCard: {
    marginTop: '8px',
    width: '100%',
    borderRadius: '8px',
    border: '1px solid var(--border-subtle)',
    background: 'var(--surface-elevated-soft)',
    padding: '10px 12px',
    transition: 'background 200ms ease-out, border-color 200ms ease-out, transform 180ms ease-out, box-shadow 200ms ease-out',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
  },
  operatorCardHover: {
    background: 'var(--surface-elevated)',
    borderColor: 'var(--accent-primary)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(59, 130, 246, 0.18)',
  },
  operatorSwitchIcon: {
    display: 'flex',
    width: '14px',
    height: '14px',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--accent-primary)',
    transition: 'opacity 180ms ease-out',
    flexShrink: 0,
  },
  operatorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    boxShadow: '0 0 0 1px var(--border-default)',
  },
  avatarFallback: {
    display: 'flex',
    width: '24px',
    height: '24px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent-deep), var(--accent-secondary))',
    fontSize: '10px',
    fontWeight: 600,
    color: 'white',
  },
  operatorInfo: {
    flex: 1,
    minWidth: 0,
  },
  operatorName: {
    display: 'block',
    margin: 0,
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  operatorEmail: {
    display: 'block',
    margin: 0,
    marginTop: '1px',
    fontSize: '11px',
    color: 'var(--text-muted)',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'color 180ms ease-out',
  },
}

// ── Icons ──────────────────────────────────────────────────────────────────────
// All 16×16 viewBox, currentColor stroke, lucide-style.

const svgProps = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }


function StreamIcon() { return (<svg width="16" height="16" viewBox="0 0 40 40" fill="none"><path d="M5 20 Q12.5 14, 20 20 T35 20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" /><path d="M5 26 Q12.5 20, 20 26 T35 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.6" /></svg>) }
function ChevronIcon() { return (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>) }
function EventsIcon() { return (<svg {...svgProps}><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" /><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" /><circle cx="12" cy="12" r="2" /><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" /><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1" /></svg>) }

function QueueIcon() { return (<svg {...svgProps}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /><path d="m9 14 2 2 4-4" /></svg>) }
function AutomationsIcon() { return (<svg {...svgProps}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>) }
// Workflow / step-graph glyph — two rounded nodes connected by an L-bend.
// Semantically right for "automation runs" because a run IS a graph of
// step executions (the detail page renders them in order).  Chosen to
// avoid the pulse-line clash with HealthIcon (the original choice) and
// to stay distinct from QueueIcon (clipboard), AutomationsIcon (sun
// rays) and SkillsIcon (stacked layers) at 16×16.
function RunsIcon() { return (<svg {...svgProps}><rect x="3" y="3" width="8" height="8" rx="2" /><path d="M7 11v4a2 2 0 0 0 2 2h4" /><rect x="13" y="13" width="8" height="8" rx="2" /></svg>) }
function MemoryIcon() { return (<svg {...svgProps}><path d="M12 2a10 10 0 1 0 10 10H12V2z" /><circle cx="12" cy="12" r="4" /></svg>) }
// Stacked-layers glyph for the Skill Library — three sheets arranged on top
// of each other, conveying "a bundle of files you can take with you".
function SkillsIcon() { return (<svg {...svgProps}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>) }
function AgentsIcon() { return (<svg {...svgProps}><circle cx="12" cy="8" r="5" /><path d="M20 21a8 8 0 1 0-16 0" /></svg>) }
function CustomersIcon() { return (<svg {...svgProps}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>) }
// Single-person glyph for the /customer (singular) detail-only page,
// distinguishing it from the two-person "Manager" icon above.
function CustomerIcon() { return (<svg {...svgProps}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>) }
// Trash glyph for the account-deletion queue page.
function DeletionsIcon() { return (<svg {...svgProps}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>) }

function SessionsIcon() { return (<svg {...svgProps}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>) }
// Playground glyph — chat bubble with a small spark, signalling "interactive chat".
function PlaygroundIcon() { return (<svg {...svgProps}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>) }
function PurchasesIcon() { return (<svg {...svgProps}><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>) }
function CatalogIcon() { return (<svg {...svgProps}><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>) }
function OfferingTemplatesIcon() { return (<svg {...svgProps}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.29 7 12 12 20.71 7" /><line x1="12" y1="22" x2="12" y2="12" /></svg>) }
function InboxIcon() { return (<svg {...svgProps}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>) }
function EmailIcon() { return (<svg {...svgProps}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>) }
function HealthIcon() { return (<svg {...svgProps}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>) }
function EmailHealthIcon() { return (<svg {...svgProps}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" /><path d="M2 20l7-7" /><path d="M22 20l-7-7" /></svg>) }
function EngineeringIcon() { return (<svg {...svgProps}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>) }
function ArtifactsIcon() { return (<svg {...svgProps}><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><polyline points="13 2 13 9 20 9" /></svg>) }
// Lucide-style "bug" glyph — matches the bug iconography used inside the Log Feature modal.
function LogFeatureIcon() { return (<svg {...svgProps}><path d="m8 2 1.88 1.88" /><path d="M14.12 3.88 16 2" /><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" /><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" /><path d="M12 20v-9" /><path d="M6.53 9C4.6 8.8 3 7.1 3 5" /><path d="M6 13H2" /><path d="M3 21c0-2.1 1.7-3.9 3.8-4" /><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" /><path d="M22 13h-4" /><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" /></svg>) }
function SettingsIcon() { return (<svg {...svgProps}><circle cx="12" cy="12" r="3" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>) }
function SwitchUserIcon() { return (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>) }
function FeedbackIcon() { return (<svg {...svgProps}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><line x1="8" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="13" y2="14" /></svg>) }

function SignUpIcon() { return (<svg {...svgProps}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>) }
// Staff Management — shield with person glyph signalling admin access control.
function StaffIcon() { return (<svg {...svgProps}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><circle cx="12" cy="10" r="3" /><path d="M7 19c0-2.76 2.24-5 5-5s5 2.24 5 5" /></svg>) }
// Bar-chart glyph for the Reports Hub — ascending bars representing analytics data.
function ReportsIcon() { return (<svg {...svgProps}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /><line x1="2" y1="20" x2="22" y2="20" /></svg>) }
function InsightsIcon() { return (<svg {...svgProps}><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></svg>) }
